import {
  useState, useRef, useEffect, ReactNode, ReactElement, ComponentType, ComponentProps,
} from 'react';
import { useDispatch } from 'react-redux';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import Table, { TableProps } from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Input from '@mui/material/Input';
import TableSortLabel from '@mui/material/TableSortLabel';
// import TablePagination from '@mui/material/TablePagination';
import Pagination from '@mui/material/Pagination';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EventIcon from '@mui/icons-material/Event';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { alpha } from '@mui/material/styles';
import type { CSSObject } from '@mui/system';

import { DATE_FORMAT_STRING, formatDate } from '#common/utils';

import TimeRangeFilter from './TimeRangeFilter';
import {
  TimeRangePresetId,
  TimeRangeValue,
  buildTimeRange,
  getTimeRangePhrase,
  isAllTime,
  resolveTimeRange,
} from './time-range.utils';

type StyleObject = CSSObject;
type SortOrder = 'asc' | 'desc';
type Setter<T> = (value: T) => unknown;

export interface EnhancedTableRow {
  id?: string | number;
  index?: number;
  [key: string]: unknown;
}

export interface EnhancedTableHeader {
  id: string;
  label?: ReactNode;
  type?: string;
  editable?: boolean;
  sortable?: boolean;
  hideForPrint?: boolean;
  defaultValue?: unknown;
  filters?: Record<string, ReactNode>;
  sx?: StyleObject;
  renderer?: (row: EnhancedTableRow, rowIndex: number, rows: EnhancedTableRow[]) => ReactNode;
  headerRenderer?: (header: EnhancedTableHeader) => ReactNode;
  onChange?: (event: unknown, meta: { rowIndex?: number | undefined }) => void;
}

export interface EnhancedTableBanner {
  position?: 'top' | 'bottom';
  renderer: (row: EnhancedTableRow) => ReactNode;
}

// Opt-in time-range filter. The page owns the value (it also feeds the list
// query, which is paginated server-side) and the table renders the control,
// the active-range chip and the empty state.
export interface EnhancedTableTimeRange {
  value: TimeRangeValue;
  onChange: (value: TimeRangeValue) => void;
  // Name of the date column being filtered, e.g. "Order Date".
  columnLabel: string;
  // Preset that Reset falls back to. Defaults to 'all'.
  defaultPreset?: Exclude<TimeRangePresetId, 'custom'> | undefined;
  // Plural noun for the empty state, e.g. "orders". Defaults to "records".
  entityLabel?: string | undefined;
}

export interface EnhancedTableProps {
  name?: string | undefined;
  banner?: EnhancedTableBanner | undefined;
  headers: EnhancedTableHeader[];
  errors?: Array<{ rowIndex: number; [key: string]: unknown }> | undefined;
  pinnedColumns?: { left?: string[] } | undefined;
  enableSort?: boolean | undefined;
  order?: SortOrder | undefined;
  setOrder?: Setter<SortOrder> | undefined;
  orderBy?: string | undefined;
  setOrderBy?: Setter<string> | undefined;
  headerFilters?: Record<string, Record<string, boolean>> | undefined;
  setHeaderFilters?: Setter<Record<string, Record<string, boolean>>> | undefined;
  getRowStyle?: ((row: EnhancedTableRow) => StyleObject) | undefined;
  hasSelectColumn?: boolean | undefined;
  canSelect?: ((row?: EnhancedTableRow) => boolean) | undefined;
  canSelectAll?: ((rows: EnhancedTableRow[]) => boolean) | undefined;
  // Explains why a row's checkbox is disabled by `canSelect`.
  selectDisabledTooltip?: ((row: EnhancedTableRow) => string) | undefined;
  selected?: Array<string | number | undefined> | undefined;
  setSelected?: Setter<Array<string | number | undefined>> | undefined;
  enableAddNew?: boolean | undefined;
  enableDelete?: ((row: EnhancedTableRow) => boolean) | undefined;
  onAddNew?: ((event: unknown, state: Record<string, unknown>) => void) | undefined;
  onDelete?: ((event: unknown, meta: { rowIndex: number }) => void) | undefined;
  addNewErrors?: Record<string, unknown> | undefined;
  pageSize?: number | undefined;
  pageIndex?: number | undefined;
  setPageIndex?: Setter<number> | undefined;
  setPageSize?: Setter<number> | undefined;
  rowCount?: number | undefined;
  timeRange?: EnhancedTableTimeRange | undefined;
  rows: EnhancedTableRow[];
}

// The spreadable data bag (rows + pagination/selection state) that page-level
// containers feed into <EnhancedTable> alongside an explicit `headers`/`name`.
export type EnhancedTableData =
  Partial<Omit<EnhancedTableProps, 'headers' | 'name' | 'rows'>>
  & { rows: EnhancedTableRow[] };

type PinnedRefs = { current: Record<string, HTMLElement | null> };

const STICKY_CELL_STYLE: StyleObject = {
  position: 'sticky',
  zIndex: 1,
  left: 0,
  backgroundColor: '#fbfbfb',
  boxShadow: '3px 0px 2px -2px #CCC',
};

const getAddNewDefaults = (props: EnhancedTableProps): Record<string, unknown> => {
  const {
    enableAddNew,
    headers,
  } = props;

  if (!enableAddNew) {
    return {};
  }

  return headers.reduce<Record<string, unknown>>((acc, h) => {
    const {
      id,
      type,
      editable,
      defaultValue,
    } = h;
    if (editable === false) {
      return acc;
    }

    if (type === 'date') {
      acc[id] = new Date();
    } else if (type === 'number') {
      acc[id] = (defaultValue === undefined)
        ? 0
        : defaultValue;
    } else {
      acc[id] = '';
    }
    return acc;
  }, {});
};

// MUI Table doesn't type the `name` attribute, but it's forwarded to the
// underlying <table> and relied on by e2e selectors (`table[name=...]`).
const NamedTable = Table as ComponentType<TableProps & { name?: string | undefined }>;

export default function EnhancedTable(props: EnhancedTableProps) {
  const {
    name,

    banner,
    headers,
    errors,

    /*
      Only support left for now, the value of the left should be ordered by the table header
      example:
      pinnedColumns: {
        left: ['id', 'createdDate']
      }
    */
    pinnedColumns,

    enableSort,
    order,
    setOrder,
    orderBy,
    setOrderBy,

    headerFilters,
    setHeaderFilters,

    getRowStyle,

    hasSelectColumn,
    canSelect,
    canSelectAll,
    selectDisabledTooltip,
    selected,
    setSelected,

    enableAddNew,
    enableDelete,
    onAddNew,
    onDelete,
    addNewErrors,

    pageSize,
    pageIndex = 0,
    setPageIndex,
    setPageSize,
    rowCount = 0,
    timeRange,
    rows,
  } = props;

  const [addNewState, setAddNewState] = useState<Record<string, unknown>>(getAddNewDefaults(props));
  const [goToPageValue, setGoToPageValue] = useState('');
  const [pageInputError, setPageInputError] = useState('');
  const dispatch = useDispatch() as unknown as (action: unknown) => void;

  const pinnedRefs = useRef<Record<string, HTMLElement | null>>({});

  const selectedList = selected ?? [];

  // A setter is either a redux action creator (calling it returns an action
  // object that must be dispatched) or a plain setState function (returns void).
  // Detecting which by `fn.name === 'actionCreator'` breaks in a minified
  // production build, where the name is mangled and the redux branch is never
  // taken — silently dropping selection/sort updates. Inspect the return value
  // instead: an action creator yields a `{ type }` object to forward to dispatch.
  const applySetter = <T, >(setter: Setter<T>, value: T) => {
    const result = setter(value);
    if (result && typeof result === 'object' && 'type' in result) {
      dispatch(result);
    }
  };

  const hasCustomAction = headers.some((h) => h.id === 'actions');

  const getStickyCellStyle = (
    id: string | undefined,
    refs: PinnedRefs,
    sx: StyleObject = {},
  ): StyleObject => {
    if (!id || !refs) {
      return sx;
    }

    if (!pinnedColumns || !pinnedColumns.left) {
      return sx;
    }

    const list = [...pinnedColumns.left];
    if (hasSelectColumn) {
      list.unshift('__');
    }

    if (!list.includes(id)) {
      return sx;
    }

    const { current } = refs;
    let left = 0;
    list.some((cid) => {
      if (cid === id) {
        return true;
      }
      const ele = current[cid];
      if (!ele) {
        return true;
      }
      left += (ele.clientWidth + 1);
      return false;
    });

    return {
      ...sx,
      ...STICKY_CELL_STYLE,
      left: `${left}px`,
    };
  };

  useEffect(() => {
    if (!pinnedColumns || !pinnedColumns.left) {
      return;
    }

    // In some scene, the pinned header will be
    // resized due to the table content change.
    // Recalculate the pinned header left value for each rerender.
    pinnedColumns.left.forEach((h) => {
      pinnedRefs.current.main
        ?.querySelectorAll(`[data-key="${h}"]`).forEach((el) => {
          const pos = getStickyCellStyle(h, pinnedRefs, {});
          (el as HTMLElement).style.left = String(pos.left ?? '');
          el.classList.add('sticky-col');
        });
    });
  });

  // render the first checkbox header if necessarly
  const renderSelectColumnHeaderCell = () => {
    if (!hasSelectColumn) {
      return null;
    }

    if (!setSelected) {
      throw new Error('select column requires setSelected');
    }

    const disabled = canSelect ? !canSelectAll?.(rows) : false;

    const headerCellStyles: StyleObject = {
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      ...getStickyCellStyle('__', pinnedRefs, {}),
    };

    // Header state reflects only this page's selectable rows.
    const selectableIds = rows.filter((r) => !canSelect || canSelect(r)).map((r) => r.id);
    const selectedOnPage = selectableIds.filter((id) => selectedList.includes(id)).length;
    const selectAllChecked = selectedOnPage > 0 && selectedOnPage === selectableIds.length;
    const indeterminate = selectedOnPage > 0 && !selectAllChecked;

    return (
      <TableCell
        key="select"
        sx={headerCellStyles}
        ref={(ref) => {
          pinnedRefs.current.__ = ref as HTMLElement | null;
        }}
      >
        <Checkbox
          disabled={disabled}
          checked={selectAllChecked}
          indeterminate={indeterminate}
          onChange={(event, checked) => {
            let newSelected: Array<string | number | undefined>;
            const ids = rows.map((r) => r.id);
            if (checked) {
              newSelected = ids.filter((id) => {
                const find = rows.find((r) => r.id === id);
                return canSelect
                  ? canSelect(find)
                  : true;
              });
            } else {
              newSelected = selectedList.filter((s) => !ids.includes(s));
            }

            applySetter(setSelected, newSelected);
          }}
        />
      </TableCell>
    );
  };

  const renderSelectColumnRowCell = (row: EnhancedTableRow) => {
    if (!hasSelectColumn) {
      return null;
    }

    if (!setSelected) {
      throw new Error('select column requires setSelected');
    }

    const disabled = canSelect ? !canSelect(row) : false;

    const headerCellStyles: StyleObject = {
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      width: '50px',
      ...getStickyCellStyle('__', pinnedRefs, {}),
    };

    const checkbox = (
      <Checkbox
        checked={selectedList.includes(row.id)}
        disabled={disabled}
        onChange={(event, checked) => {
          const newSelected = checked
            ? [...selectedList, row.id]
            : selectedList.filter((s) => s !== row.id);

          applySetter(setSelected, newSelected);
        }}
      />
    );

    return (
      <TableCell key="select" sx={headerCellStyles}>
        {disabled && selectDisabledTooltip
          ? (
            <Tooltip title={selectDisabledTooltip(row)}>
              <span>{checkbox}</span>
            </Tooltip>
          )
          : checkbox}
      </TableCell>
    );
  };

  const renderTableHeaderCell = ({
    header,
  }: { header: EnhancedTableHeader; headerIndex?: number }) => {
    const {
      id,
      label,
      filters,
      sortable,
      headerRenderer,
      hideForPrint,
      sx = {},
    } = header;

    const tableCellProps: {
      sx: StyleObject;
      sortDirection?: SortOrder | false;
    } = {
      sx: {
        font: '500 13px/1.4 "Roboto","Helvetica","Arial",sans-serif',
        color: 'rgba(0, 0, 0, 0.6)',
        padding: '12px 14px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        whiteSpace: 'nowrap',
        ...getStickyCellStyle(id, pinnedRefs, sx),
        ...(hideForPrint && { displayPrint: 'none' }),
        ...(id === 'actions' && { displayPrint: 'none' }),
      },
    };

    if (enableSort && (!setOrder || !setOrderBy)) {
      throw new Error('sortable table requires setOrder and setOrderBy');
    }

    if (filters && (!setHeaderFilters)) {
      throw new Error('header filter requires setHeaderFilters');
    }

    let headerContentElement: ReactNode = label;
    if (headerRenderer) {
      headerContentElement = headerRenderer(header);
    }

    if (filters) {
      const allFilterKeys = Object.keys(filters);
      let currentFilter: Record<string, boolean> = (headerFilters && headerFilters[id]) || {};

      const colorProps: { sx?: StyleObject } = {};
      if (Object.keys(currentFilter).length) {
        colorProps.sx = {
          color: '#1976d2',
        };
      }

      // column value filters
      headerContentElement = (
        <FormControl
          variant="standard"
          onClick={(event) => event.stopPropagation()}
        >
          <Select
            value={-1}
            sx={{ fontSize: 'small', fontWeight: 'bold' }}
            onClick={(event) => event.stopPropagation()}
            IconComponent={(_props) => (<FilterAltIcon fontSize="small" {...colorProps} />)}
            disableUnderline
          >
            <MenuItem value={-1}>{label}</MenuItem>
            {
              allFilterKeys.map((key) => {
                const checked = (currentFilter && currentFilter[key]) || false;
                return (
                  <MenuItem key={key} value={key}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={checked}
                          onChange={(event) => {
                            currentFilter = {
                              ...currentFilter,
                              [key]: event.target.checked,
                            };
                            const newValue: Record<string, Record<string, boolean>> = {
                              ...headerFilters,
                              [id]: currentFilter,
                            };
                            if (allFilterKeys.every((k) => currentFilter[k] !== true)) {
                              delete newValue[id];
                            }
                            dispatch(setHeaderFilters!(newValue));
                          }}
                        />
                      )}
                      label={filters[key]}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </MenuItem>
                );
              })
            }
          </Select>
        </FormControl>
      );
    }

    if (setOrder && setOrderBy && (enableSort && sortable !== false)) {
      tableCellProps.sortDirection = (orderBy === id ? (order ?? false) : false);
      headerContentElement = (
        <TableSortLabel
          active={orderBy === id}
          direction={orderBy === id ? order : 'desc'}
          onClick={() => {
            const isDesc = orderBy === id && order === 'desc';

            applySetter(setOrder, isDesc ? 'asc' : 'desc');
            applySetter(setOrderBy, id);
          }}
        >
          {headerContentElement}
        </TableSortLabel>
      );
    }

    return (
      <TableCell
        key={id}
        ref={(ref) => {
          if (pinnedColumns && pinnedColumns.left && pinnedColumns.left.includes(id)) {
            pinnedRefs.current[id] = ref as HTMLElement | null;
          }
        }}
        {...tableCellProps}
        data-key={header.id}
      >
        {headerContentElement}
      </TableCell>
    );
  };

  const renderTableRowCell = ({
    header,
    row,
    rowIndex,
    tableCellStyles = {},
  }: {
    header: EnhancedTableHeader;
    row: EnhancedTableRow;
    rowIndex: number;
    headerIndex?: number;
    tableCellStyles?: StyleObject;
  }) => {
    const { id, hideForPrint } = header;
    const sx: StyleObject = {
      ...tableCellStyles,
      ...(header.sx || {}),
    };
    const tableCellProps = {
      sx: {
        ...getStickyCellStyle(id, pinnedRefs, sx),
        ...(hideForPrint && { displayPrint: 'none' }),
        ...(id === 'actions' && { displayPrint: 'none' }),
      },
    };

    const error = errors && !!errors.find((err) => err.rowIndex === row.index);
    if (header.editable && header.type === 'number') {
      return (
        <TableCell
          key={id}
          {...tableCellProps}
        >
          <FormControl>
            <Input
              type="number"
              value={row[header.id]}
              error={error}
              onChange={(e) => {
                // TODO only support product edit variants
                header.onChange?.(e, { rowIndex: row.index });
              }}
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
            />
          </FormControl>
        </TableCell>
      );
    }

    let cellContent: ReactNode = row[header.id] as ReactNode;
    if (header.renderer) {
      cellContent = header.renderer(row, rowIndex, rows);
    }

    return (
      <TableCell
        key={id}
        {...tableCellProps}
        data-key={header.id}
      >
        {cellContent}
      </TableCell>
    );
  };

  const renderAddNewRowCell = ({
    header,
  }: { header: EnhancedTableHeader; headerIndex?: number }) => {
    let cellContent: ReactNode = null;

    if (header.id === 'actions' && !header.renderer) {
      throw new Error('renderer() is required for actions header.');
    }

    if (header.id === 'actions') {
      cellContent = (header.renderer as () => ReactNode)();
    } else if (header.editable === false) {
      cellContent = addNewState[header.id] as ReactNode;
    } else if (header.type === 'multiline') {
      cellContent = (
        <FormControl fullWidth>
          <Input
            type={header.type}
            // size="small"
            multiline
            fullWidth
            rows={2}
            error={!!(addNewErrors && addNewErrors[header.id])}
            value={addNewState[header.id]}
            onChange={(event) => {
              setAddNewState({
                ...addNewState,
                [header.id]: event.target.value,
              });
            }}
          />
        </FormControl>
      );
    } else if (header.type === 'number' || header.type === undefined) {
      cellContent = (
        <FormControl>
          <Input
            type={header.type}
            size="small"
            error={!!(addNewErrors && addNewErrors[header.id])}
            value={addNewState[header.id]}
            onChange={(event) => {
              setAddNewState({
                ...addNewState,
                [header.id]: event.target.value,
              });
            }}
          />
        </FormControl>
      );
    } else if (header.type === 'date') {
      cellContent = (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
        >
          <DesktopDatePicker
            format={DATE_FORMAT_STRING}
            value={addNewState[header.id] as Date | null}
            onChange={(d) => {
              setAddNewState({
                ...addNewState,
                [header.id]: d,
              });
            }}
            sx={{ minWidth: 70 }}
            slotProps={{
              textField: {
                size: 'small',
                'data-type': header.type,
                'data-value': String((addNewState[header.id] as string | number | undefined) ?? ''),
              },
            } as NonNullable<ComponentProps<typeof DesktopDatePicker>['slotProps']>}
          />
        </LocalizationProvider>
      );
    }

    return (
      <TableCell
        key={header.id}
        sx={header.sx || {}}
      >
        {cellContent}
      </TableCell>
    );
  };

  const renderPagination = () => {
    if (!pageSize) {
      return null;
    }

    const pageCount = Math.max(1, Math.ceil(rowCount / pageSize));
    const fromRow = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
    const toRow = Math.min((pageIndex + 1) * pageSize, rowCount);

    const handleGoToPage = (value: string) => {
      const pageNumber = parseInt(value, 10);
      if (isNaN(pageNumber) || value.trim() === '') {
        setPageInputError('Please enter a valid page number');
        return;
      }
      if (pageNumber < 1) {
        setPageInputError('Page number must be at least 1');
        return;
      }
      if (pageNumber > pageCount) {
        setPageInputError(`Page number cannot exceed ${pageCount}`);
        return;
      }
      setPageInputError('');
      const pageAction = setPageIndex?.(pageNumber - 1);
      // Dispatch if it's a Redux action; otherwise assume the setter handled it (state update or dispatch)
      if (pageAction && typeof pageAction === 'object' && 'type' in pageAction) {
        dispatch(pageAction);
      }
      setGoToPageValue('');
    };

    const labelSx: StyleObject = { font: '400 13px/1.4 "Roboto","Helvetica","Arial",sans-serif', color: 'rgba(0, 0, 0, 0.6)' };
    const summarySx: StyleObject = { ...labelSx, '& b': { color: 'rgba(0, 0, 0, 0.87)', fontWeight: 500 } };

    return (
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          padding: '12px 14px',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: 1,
          backgroundColor: '#fff',
        }}
      >
        <Box component="span" sx={summarySx}>
          <b>{fromRow}</b>
          {'–'}
          <b>{toRow}</b>
          {' of '}
          <b>{rowCount}</b>
        </Box>

        {setPageSize && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, ...labelSx }}>
            <span>Rows per page</span>
            <Select
              value={pageSize}
              onChange={(event) => {
                const sizeAction = setPageSize(Number(event.target.value));
                // Dispatch if it's a Redux action; otherwise assume the setter handled it
                if (sizeAction && typeof sizeAction === 'object' && 'type' in sizeAction) {
                  dispatch(sizeAction);
                }
              }}
              variant="outlined"
              size="small"
              sx={{ height: 32, '& .MuiSelect-select': { py: 0, fontSize: 13 } }}
            >
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={70}>70</MenuItem>
            </Select>
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, ...labelSx }}>
          <span>Go to</span>
          <TextField
            size="small"
            type="number"
            value={goToPageValue}
            error={!!pageInputError}
            helperText={pageInputError || undefined}
            onChange={(e) => {
              setGoToPageValue(e.target.value);
              if (pageInputError) {
                setPageInputError('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleGoToPage((e.target as HTMLInputElement).value);
              }
            }}
            onBlur={(e) => {
              if (e.target.value) {
                handleGoToPage(e.target.value);
              }
            }}
            placeholder=""
            slotProps={{
              htmlInput: {
                min: 1,
                max: pageCount,
                'aria-label': 'Page number',
                style: { width: 56, height: 32, padding: '0 8px', textAlign: 'center', boxSizing: 'border-box', fontSize: 14 },
              },
            }}
            sx={{ '& .MuiOutlinedInput-root': { padding: 0 } }}
          />
          <span>{`/ ${pageCount}`}</span>
        </Box>

        <Pagination
          count={pageCount}
          page={pageIndex + 1}
          onChange={(event, page) => {
            const pageAction = setPageIndex?.(page - 1);
            // Dispatch if it's a Redux action; otherwise assume the setter handled it
            if (pageAction && typeof pageAction === 'object' && 'type' in pageAction) {
              dispatch(pageAction);
            }
          }}
          showFirstButton
          showLastButton
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>
    );
  };

  const renderTimeRangeToolbar = () => {
    if (!timeRange) {
      return null;
    }

    const {
      value,
      onChange,
      columnLabel,
      defaultPreset = 'all',
    } = timeRange;

    // Resetting to the default is a no-op when the range already is the
    // default, so the chip drops the clear affordance and stays informational.
    const canReset = value.preset !== defaultPreset;
    const { from, to } = resolveTimeRange(value);
    const chipLabel = `${columnLabel}: ${from ? formatDate(from) : 'Any'} – ${to ? formatDate(to) : 'Today'}`;

    return (
      <Stack
        direction="row"
        spacing={1.25}
        useFlexGap
        sx={{
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 1.5,
          displayPrint: 'none',
        }}
      >
        <TimeRangeFilter
          value={value}
          onChange={onChange}
          columnLabel={columnLabel}
          defaultPreset={defaultPreset}
        />
        {
          !isAllTime(value) && (
            <Chip
              size="small"
              icon={(
                <EventIcon
                  // Callback form: MUI resolves a palette path by splitting on
                  // '.', so 'brand_gold.dark !important' would miss the palette
                  // and emit an invalid declaration the browser drops.
                  sx={(theme) => ({ fontSize: 16, color: `${theme.palette.brand_gold.dark} !important` })}
                />
              )}
              label={chipLabel}
              {...(canReset && { onDelete: () => onChange(buildTimeRange(defaultPreset)) })}
              sx={{
                height: 41,
                bgcolor: (theme) => alpha(theme.palette.brand_gold.main, 0.14),
                border: (theme) => `1px solid ${theme.palette.brand_gold.main}`,
                fontSize: '0.8125rem',
                maxWidth: '100%',
                '& .MuiChip-deleteIcon': { color: 'brand_gold.dark' },
              }}
            />
          )
        }
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {`${rowCount} ${rowCount === 1 ? 'result' : 'results'}`}
        </Typography>
      </Stack>
    );
  };

  const renderTimeRangeEmptyState = () => {
    if (!timeRange || rows.length || isAllTime(timeRange.value)) {
      return null;
    }

    const {
      value,
      onChange,
      defaultPreset = 'all',
      entityLabel = 'records',
    } = timeRange;

    // Same rule as the chip: resetting to the default is a no-op when the range
    // already is the default, so don't offer it — and don't tell the user to do
    // the thing they have already done.
    const canReset = value.preset !== defaultPreset;
    let hint = 'Try a wider time range.';
    if (canReset) {
      hint = defaultPreset === 'all'
        ? 'Reset the time range to see everything.'
        : `Widen the time range or reset it to ${getTimeRangePhrase(defaultPreset)}.`;
    }

    return (
      <Stack spacing={1.25} sx={{ alignItems: 'center', px: 3, py: 7, whiteSpace: 'normal', textAlign: 'center' }}>
        <EventBusyIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
        <Typography variant="subtitle2">{`No ${entityLabel} in this time range`}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {hint}
        </Typography>
        {
          canReset && (
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 0.75 }}
              onClick={() => onChange(buildTimeRange(defaultPreset))}
            >
              Reset time range
            </Button>
          )
        }
      </Stack>
    );
  };

  const tableRows: ReactNode[] = rows.map((row, ridx) => {
    const key = (row.id === undefined)
      ? `index_${ridx}`
      : `id_${row.id}`;

    const defaultRowStyle: StyleObject = {
      ':hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
      ':last-of-type td': { borderBottom: 0 },
    };

    const rowStyle: StyleObject = getRowStyle ? {
      ...defaultRowStyle,
      ...getRowStyle(row),
    } : defaultRowStyle;

    const tableRowCellStyles: StyleObject = {
      textDecoration: 'inherit',
      font: '400 14px/1.4 "Roboto","Helvetica","Arial",sans-serif',
      color: 'rgba(0, 0, 0, 0.87)',
      padding: '12px 14px',
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    };

    const bannerRowCellStyles: StyleObject = {};
    if (banner) {
      if (banner.position === 'bottom') {
        tableRowCellStyles.borderBottom = 'none';
      } else {
        bannerRowCellStyles.borderBottom = 'none';
      }
    }

    const tableCells: Array<ReactElement | null> = [renderSelectColumnRowCell(row)];
    headers.forEach((h, hidx) => {
      const cell = renderTableRowCell({
        header: h,
        headerIndex: hidx,
        row,
        rowIndex: ridx,
        tableCellStyles: tableRowCellStyles,
      });
      tableCells.push(cell);
    });

    if (enableAddNew && !hasCustomAction) {
      const disabled = (enableDelete && !enableDelete(row));
      tableCells.push(
        <TableCell
          key="add-action"
          sx={{ ...tableRowCellStyles, displayPrint: 'none' }}
        >
          <Link
            component="button"
            disabled={disabled}
            className={disabled ? 'disabled-link' : ''}
            onClick={disabled ? () => {} : (event) => onDelete?.(event, { rowIndex: row.index || ridx })}
          >
            Delete
          </Link>
        </TableCell>,
      );
    }

    const tableRow: ReactElement[] = [
      <TableRow
        key={key}
        sx={rowStyle}
      >
        {tableCells}
      </TableRow>,
    ];

    let rowBanner: ReactElement;
    if (banner) {
      rowBanner = (
        <TableRow key={`header_${key}`}>
          <TableCell colSpan={5} sx={bannerRowCellStyles}>
            {banner.renderer(row)}
          </TableCell>
        </TableRow>
      );

      if (banner.position === 'bottom') {
        tableRow.push(rowBanner);
      } else {
        tableRow.unshift(rowBanner);
      }
    }

    return tableRow;
  });

  if (enableAddNew) {
    const addNewRowElements: ReactNode[] = headers.map((h, hidx) => renderAddNewRowCell({
      header: h,
      headerIndex: hidx,
    }));
    if (!hasCustomAction) {
      addNewRowElements.push(
        <TableCell key="add-button" sx={{ displayPrint: 'none' }}>
          <Box>
            <Link
              component="button"
              onClick={(event) => {
                onAddNew?.(event, addNewState);
                setAddNewState(getAddNewDefaults(props));
              }}
            >
              Add
            </Link>
          </Box>
        </TableCell>,
      );
    }

    tableRows.push((
      <TableRow key="add-row" sx={{ displayPrint: 'none' }}>
        {addNewRowElements}
      </TableRow>
    ));
  }

  const headerCells = (
    <TableRow>
      {renderSelectColumnHeaderCell()}
      {
        headers.map((h, hidx) => renderTableHeaderCell({
          header: h,
          headerIndex: hidx,
        }))
      }
      {
        (enableAddNew && !hasCustomAction) && (
          <TableCell key="add-action" sx={{ displayPrint: 'none' }}>
            Action
          </TableCell>
        )
      }
    </TableRow>
  );

  return (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      {renderTimeRangeToolbar()}
      <TableContainer
        component={Paper}
        variant="outlined"
        ref={(ref) => { pinnedRefs.current.main = ref; }}
        sx={{ borderRadius: 1, borderColor: 'rgba(0, 0, 0, 0.12)' }}
      >
        <NamedTable
          name={name}
          size="small"
          sx={{ borderCollapse: 'separate' }}
        >
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            {headerCells}
          </TableHead>
          <TableBody>
            {tableRows}
          </TableBody>
        </NamedTable>
        {renderTimeRangeEmptyState()}
      </TableContainer>
      {renderPagination()}
    </Box>
  );
}
